'use client';

import { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useWbsStore } from '@/stores/wbsStore';
import type { Task, TaskStatus } from '@/types';

interface KanbanBoardProps {
  onEditTask: (task: Task) => void;
}

const COLUMNS: { status: TaskStatus; label: string; dot: string; headerBg: string; countBg: string; countText: string }[] = [
  { status: '시작전', label: '시작전', dot: 'bg-gray-400', headerBg: 'bg-gray-100', countBg: 'bg-gray-200', countText: 'text-gray-700' },
  { status: '진행중', label: '진행중', dot: 'bg-blue-500', headerBg: 'bg-blue-50', countBg: 'bg-blue-100', countText: 'text-blue-700' },
  { status: '컨펌중', label: '컨펌중', dot: 'bg-amber-500', headerBg: 'bg-amber-50', countBg: 'bg-amber-100', countText: 'text-amber-700' },
  { status: '완료', label: '완료', dot: 'bg-emerald-500', headerBg: 'bg-emerald-50', countBg: 'bg-emerald-100', countText: 'text-emerald-700' },
];

const STAGE_COLORS: Record<string, { bg: string; text: string; bar: string }> = {
  '착수': { bg: 'bg-purple-50', text: 'text-purple-600', bar: 'bg-purple-400' },
  '분석': { bg: 'bg-blue-50', text: 'text-blue-600', bar: 'bg-blue-400' },
  '설계': { bg: 'bg-cyan-50', text: 'text-cyan-600', bar: 'bg-cyan-400' },
  '구현': { bg: 'bg-green-50', text: 'text-green-600', bar: 'bg-green-400' },
  '테스트': { bg: 'bg-yellow-50', text: 'text-yellow-600', bar: 'bg-yellow-400' },
  '이행': { bg: 'bg-orange-50', text: 'text-orange-600', bar: 'bg-orange-400' },
  '운영': { bg: 'bg-red-50', text: 'text-red-600', bar: 'bg-red-400' },
  '종료': { bg: 'bg-gray-50', text: 'text-gray-600', bar: 'bg-gray-400' },
  '보고': { bg: 'bg-indigo-50', text: 'text-indigo-600', bar: 'bg-indigo-400' },
};

function getDDay(endDate: string | null): { text: string; urgent: boolean } | null {
  if (!endDate) return null;
  const diff = Math.ceil((new Date(endDate).getTime() - Date.now()) / 86400000);
  if (diff < 0) return { text: `D+${Math.abs(diff)}`, urgent: true };
  if (diff === 0) return { text: 'D-Day', urgent: true };
  if (diff <= 3) return { text: `D-${diff}`, urgent: true };
  return { text: `D-${diff}`, urgent: false };
}

// 카드
function KanbanCard({ task, onEdit, isDragOverlay }: { task: Task; onEdit: (t: Task) => void; isDragOverlay?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task.id,
    data: { task },
  });

  const style = isDragOverlay ? {} : { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.2 : 1 };
  const dday = getDDay(task.end_date);
  const stage = STAGE_COLORS[task.stage_name] || { bg: 'bg-gray-50', text: 'text-gray-600', bar: 'bg-gray-400' };

  return (
    <div
      ref={isDragOverlay ? undefined : setNodeRef}
      style={style}
      className={`group bg-white rounded-lg cursor-grab active:cursor-grabbing transition-shadow ${
        isDragOverlay ? 'shadow-2xl ring-2 ring-blue-500/40 scale-[1.02]' : 'shadow-sm hover:shadow-md border border-gray-200/80'
      }`}
      {...(isDragOverlay ? {} : { ...listeners, ...attributes })}
    >
      <div className="px-3 py-2.5">
        {/* 상단: 단계 + D-Day */}
        <div className="flex items-center justify-between mb-1">
          <span className={`text-[10px] font-medium ${stage.text}`}>{task.stage_name}</span>
          {dday && (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
              dday.urgent ? 'bg-red-50 text-red-600' : 'text-gray-400'
            }`}>
              {dday.text}
            </span>
          )}
        </div>

        {/* 작업명 */}
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(task); }}
          className="text-[13px] font-semibold text-gray-800 hover:text-blue-600 text-left w-full leading-snug line-clamp-2 transition-colors"
          title={task.task_name}
        >
          {task.task_name}
        </button>

        {/* 하단: 담당자 + 공정율 */}
        <div className="flex items-center justify-between mt-2.5">
          {task.manager ? (
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <span className="text-[9px] font-bold text-gray-500">{task.manager.charAt(0)}</span>
              </div>
              <span className="text-[11px] text-gray-500">{task.manager}</span>
            </div>
          ) : (
            <span className="text-[11px] text-gray-300">미배정</span>
          )}
          <div className="flex items-center gap-1.5">
            <div className="w-12 h-1 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  task.progress >= 100 ? 'bg-emerald-500' : task.progress >= 50 ? 'bg-blue-500' : 'bg-amber-400'
                }`}
                style={{ width: `${Math.min(task.progress, 100)}%` }}
              />
            </div>
            <span className={`text-[10px] font-bold tabular-nums ${
              task.progress >= 100 ? 'text-emerald-600' : 'text-gray-400'
            }`}>{task.progress}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// 컬럼
function KanbanColumn({
  status, label, dot, headerBg, countBg, countText, tasks, onEdit,
}: {
  status: TaskStatus; label: string; dot: string; headerBg: string; countBg: string; countText: string;
  tasks: Task[]; onEdit: (t: Task) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col min-w-[260px] flex-1 rounded-lg bg-[#f8f9fb] transition-all ${
        isOver ? 'bg-blue-50/60 ring-2 ring-blue-400/50 ring-inset' : ''
      }`}
    >
      {/* 헤더 */}
      <div className={`flex items-center gap-2.5 px-3 py-3 rounded-t-lg ${headerBg}`}>
        <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
        <h3 className="text-sm font-bold text-gray-700">{label}</h3>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${countBg} ${countText}`}>
          {tasks.length}
        </span>
      </div>

      {/* 카드 영역 */}
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-2 min-h-[80px]">
        {tasks.length === 0 ? (
          <div className={`flex items-center justify-center h-16 text-xs rounded-lg border-2 border-dashed transition-colors ${
            isOver ? 'border-blue-300 text-blue-400 bg-blue-50/40' : 'border-gray-200 text-gray-300'
          }`}>
            여기에 드래그
          </div>
        ) : (
          tasks.map(task => (
            <KanbanCard key={task.id} task={task} onEdit={onEdit} />
          ))
        )}
      </div>
    </div>
  );
}

export default function KanbanBoard({ onEditTask }: KanbanBoardProps) {
  const { tasks, updateTask } = useWbsStore();
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const kanbanTasks = useMemo(() => tasks.filter(t => t.depth === 3), [tasks]);

  const grouped = useMemo(() => {
    const map: Record<string, Task[]> = { '시작전': [], '진행중': [], '컨펌중': [], '완료': [] };
    kanbanTasks.forEach(t => {
      if (map[t.status]) map[t.status].push(t);
      else map['시작전'].push(t);
    });
    return map;
  }, [kanbanTasks]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveTask(event.active.data.current?.task as Task || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveTask(null);
    const { active, over } = event;
    if (!over) return;

    const task = active.data.current?.task as Task;
    const newStatus = over.id as TaskStatus;

    if (task && newStatus && task.status !== newStatus) {
      const updateData: Partial<Task> = { status: newStatus };
      if (newStatus === '완료') updateData.progress = 100;
      if (task.status === '완료' && newStatus !== '완료' && task.progress === 100) {
        updateData.progress = 90;
      }
      await updateTask(task.id, updateData);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-3 h-full p-4 overflow-x-auto">
        {COLUMNS.map(col => (
          <KanbanColumn
            key={col.status}
            status={col.status}
            label={col.label}
            dot={col.dot}
            headerBg={col.headerBg}
            countBg={col.countBg}
            countText={col.countText}
            tasks={grouped[col.status] || []}
            onEdit={onEditTask}
          />
        ))}
      </div>

      <DragOverlay>
        {activeTask ? (
          <div className="w-[240px]">
            <KanbanCard task={activeTask} onEdit={() => {}} isDragOverlay />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
